"use client";

// Watching: every video the visitor played and where they stopped, newest first. Kept in
// localStorage like the other lists; once signed in it is mirrored to the account
// (/api/me/watching), and what was watched before signing in is merged in, the latest position winning.

import { useSyncExternalStore } from "react";
import type { VideoSummary } from "@/lib/api/types";

export interface WatchEntry {
  video: VideoSummary;
  /** Seconds into the video where playback stopped. */
  position: number;
  /** Length in seconds, as the player measured it. */
  duration: number;
  /** When it was last watched, in ms. */
  at: number;
}

const KEY = "sweet.watching";
const MAX = 500;
/** While playing, the account copy is updated at most this often; pauses, the end and leaving the page update it at once. */
const SYNC_EVERY = 15_000;
const EMPTY: WatchEntry[] = [];

let cache: WatchEntry[] | null = null;
const listeners = new Set<() => void>();
let remote = false;
const lastSent = new Map<string, number>();

function read(): WatchEntry[] {
  if (cache) return cache;
  let value = EMPTY;
  try {
    const parsed = JSON.parse(window.localStorage.getItem(KEY) ?? "[]");
    if (Array.isArray(parsed)) value = parsed.filter((e) => e?.video?.id && Number.isFinite(e.position));
  } catch {
    // Storage blocked or corrupt: start empty.
  }
  cache = value;
  return value;
}

function write(value: WatchEntry[]) {
  cache = value;
  try {
    window.localStorage.setItem(KEY, JSON.stringify(value));
  } catch {
    // Keep the in-memory copy for this session.
  }
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  const onStorage = (e: StorageEvent) => {
    if (e.key !== KEY) return;
    cache = null;
    listener();
  };
  window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", onStorage);
  };
}

function post(body: unknown) {
  return fetch("/api/me/watching", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body) });
}

export function useWatching(): WatchEntry[] {
  return useSyncExternalStore(subscribe, read, () => EMPTY);
}

/** Watched to (nearly) the end, i.e. into the last 10 s or 2% (but never more than 10% of it): the next play starts over. */
export function isFinished(e: Pick<WatchEntry, "position" | "duration">) {
  return e.duration > 0 && e.position >= e.duration - Math.min(Math.max(10, e.duration * 0.02), e.duration * 0.1);
}

/** Where to pick a video up again: where it was left, or 0 if it was barely started or finished. */
export function resumePosition(id: string): number {
  const e = read().find((x) => x.video.id === id);
  return e && e.position >= 5 && !isFinished(e) ? e.position : 0;
}

/** Where to start a video: ?t=754 in the address, else where this visitor left it (0 before hydration). */
export function useResumePosition(id: string | undefined): number {
  return useSyncExternalStore(
    subscribe,
    () => {
      const t = Number(new URLSearchParams(window.location.search).get("t"));
      if (Number.isFinite(t) && t > 0) return t;
      return id ? resumePosition(id) : 0;
    },
    () => 0,
  );
}

/** Records where playback stands. `now` also updates the account right away (pause, end, leaving the page). */
export function recordProgress(video: VideoSummary, position: number, duration: number, now = false) {
  const entry: WatchEntry = { video, position: Math.max(0, Math.floor(position)), duration: Math.round(duration) || 0, at: Date.now() };
  write([entry, ...read().filter((e) => e.video.id !== video.id)].slice(0, MAX));
  if (!remote) return;
  if (!now && Date.now() - (lastSent.get(video.id) ?? 0) < SYNC_EVERY) return;
  lastSent.set(video.id, Date.now());
  const body = { op: "progress", id: video.id, position: entry.position, duration: entry.duration };
  // A beacon still goes out when the page is being closed; a fetch might not.
  if (now && navigator.sendBeacon?.("/api/me/watching", new Blob([JSON.stringify(body)], { type: "application/json" }))) return;
  void post(body).catch(() => {});
}

export function forgetVideo(id: string) {
  write(read().filter((e) => e.video.id !== id));
  if (remote) void post({ op: "remove", id }).catch(() => {});
}

export function clearWatching() {
  write(EMPTY);
  if (remote) void post({ op: "clear" }).catch(() => {});
}

/** On sign-out: drop this device's copy so the next person starts clean. */
export function clearLocalWatching() {
  remote = false;
  lastSent.clear();
  write(EMPTY);
}

/** Adopts the account's list, uploading anything watched more recently on this device. */
export async function syncWatching() {
  const res = await fetch("/api/me/watching", { cache: "no-store" });
  const data: { authenticated: true; items: WatchEntry[] } | { authenticated: false } = res.ok ? await res.json() : { authenticated: false };
  if (!data.authenticated) {
    remote = false;
    return;
  }
  remote = true;
  let server = data.items;
  const newer = read().filter((l) => {
    const s = server.find((x) => x.video.id === l.video.id);
    return !s || s.at < l.at;
  });
  if (newer.length) {
    const merged = await post({ op: "merge", items: newer.map((e) => ({ id: e.video.id, position: e.position, duration: e.duration, at: e.at })) });
    if (merged.ok) server = (await merged.json()).items;
  }
  write(server);
}
